import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

const VARIANT_CONFIG = {
  success: { icon: 'check', bg: 'bg-success-dark', text: 'text-content-inverse' },
  error: { icon: 'warning', bg: 'bg-error', text: 'text-content-inverse' },
  info: { icon: 'info', bg: 'bg-primary', text: 'text-content-inverse' },
  warning: { icon: 'warning', bg: 'bg-warning-dark', text: 'text-content-inverse' },
};

// `action` (optional): { label, onClick } renders an inline button (e.g. "Undo").
// When present, the toast body no longer dismisses on click — only the action or
// the auto-timeout closes it — so the action stays the deliberate target.
function Toast({ message, variant = 'success', duration = 3000, onClose, icon, action }) {
  const [isExiting, setIsExiting] = useState(false);
  // The latest onClose: most callers pass a new function each render, and
  // that must not restart the clock (a busy page held its toast up).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  // The exit animation's wait, cleared when the toast goes: a toast replaced
  // on its way out (a new key) must not close the one that took its place.
  const exitTimer = useRef(null);
  const leave = useCallback(() => {
    setIsExiting(true);
    clearTimeout(exitTimer.current);
    exitTimer.current = setTimeout(() => onCloseRef.current?.(), 200); // Wait for exit animation
  }, []);
  useEffect(() => () => clearTimeout(exitTimer.current), []);
  const dismiss = () => { if (!isExiting) leave(); };

  // Its full time from when it shows. A caller without a key shows the next
  // toast in this one: a new message starts the clock again.
  const shown = typeof message === 'string' || typeof message === 'number' ? message : null;
  useEffect(() => {
    setIsExiting(false);
    clearTimeout(exitTimer.current);
    const timer = setTimeout(leave, duration);
    return () => clearTimeout(timer);
  }, [duration, shown, leave]);

  const config = VARIANT_CONFIG[variant];
  const iconName = icon || config.icon;

  const content = (
    <>
      <Icon name={iconName} className={`w-5 h-5 flex-shrink-0 ${variant === 'success' ? 'animate-success-check' : ''}`} />
      <span>{message}</span>
    </>
  );

  const toast = (
    <div
      // w-max + a viewport cap: a fixed box at left-1/2 otherwise shrinks to
      // the half-screen to its right, so a longer message wrapped into a
      // narrow column.
      // The centring transform stays on this box; the slide animates the inner
      // one — a keyframe `transform` on the same box replaced the centring,
      // so every toast entered from the right half of the screen.
      className="fixed bottom-[calc(var(--app-tabbar-h)+1.5rem)] left-1/2 -translate-x-1/2 w-max max-w-[calc(100vw-2rem)] z-toast"
      role="status"
      aria-live="polite"
      // Its action is reachable by Tab from inside an open modal (Modal's trap).
      data-modal-companion={action ? '' : undefined}
    >
      <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-elevation-4 ${config.bg} ${config.text} text-sm font-medium transition-all duration-normal ${
        isExiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0 animate-slide-up'
      }`}>
        {action ? (
          <span className="flex items-center gap-2.5">{content}</span>
        ) : (
          <button
            onClick={dismiss}
            className={`flex items-center gap-2.5 cursor-pointer border-none bg-transparent ${config.text} text-sm font-medium`}
          >
            {content}
          </button>
        )}
        {action && (
          <button
            // Not once it has run out: the change it answers may be gone (Undo).
            onClick={() => { if (isExiting) return; action.onClick(); dismiss(); }}
            className="ml-1 -my-1 px-2.5 py-1 rounded-lg font-semibold underline underline-offset-2 hover:bg-content-inverse/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-inverse/50"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(toast, document.body);
}

export default Toast;
